import type { Action } from './actions.js'
import type { Emitter } from './emitters.js'
import type { Rule } from './rules.js'
import type { Scope } from './primitives.js'
import type { Trigger } from './triggers.js'
import { SERVER_ONLY_TRIGGER_TYPES } from './triggers.js'
import type { WorldDefinition } from './world.js'

export type IssueSeverity = 'error' | 'warning'

export interface ValidationIssue {
  severity: IssueSeverity
  code: string
  message: string
  /** Where the problem is, e.g. `rules[2].actions[0].emitterId`. */
  path: string
}

/**
 * Below this radius an outdoor trigger is smaller than everyday GPS error, so
 * it will fire late, early, or not at all. The studio warns rather than blocks,
 * because indoor and simulated worlds have every right to be small.
 */
export const MIN_RELIABLE_OUTDOOR_RADIUS_M = 10

type RefKind = 'region' | 'gate' | 'course' | 'emitter' | 'rule' | 'cue' | 'bus'

interface Ref {
  kind: RefKind
  id: string
}

function triggerRefs(trigger: Trigger): Ref[] {
  switch (trigger.type) {
    case 'zone':
    case 'dwell':
      return [{ kind: 'region', id: trigger.regionId }]
    case 'proximity':
      return trigger.target.kind === 'emitter'
        ? [{ kind: 'emitter', id: trigger.target.emitterId }]
        : []
    case 'gate':
      return [{ kind: 'gate', id: trigger.gateId }]
    case 'chainage':
      return [{ kind: 'course', id: trigger.courseId }]
    case 'sequence':
      return trigger.ruleIds.map((id) => ({ kind: 'rule' as const, id }))
    case 'ruleFired':
      return [{ kind: 'rule', id: trigger.ruleId }]
    case 'emitterState':
      return [{ kind: 'emitter', id: trigger.emitterId }]
    case 'operatorCue':
      return [{ kind: 'cue', id: trigger.cueId }]
    case 'speed':
    case 'motion':
    case 'elapsed':
    case 'wallClock':
    case 'variable':
    case 'random':
    case 'accuracy':
    case 'session':
      return []
  }
}

function actionRefs(action: Action): Ref[] {
  switch (action.type) {
    case 'play':
    case 'stop':
    case 'setPlayback':
    case 'moveEmitter':
    case 'pathControl':
      return [{ kind: 'emitter', id: action.emitterId }]
    case 'fadeTo':
      return action.target.kind === 'emitter'
        ? [{ kind: 'emitter', id: action.target.emitterId }]
        : [{ kind: 'bus', id: action.target.busId }]
    case 'crossfade':
      return [
        { kind: 'emitter', id: action.fromEmitterId },
        { kind: 'emitter', id: action.toEmitterId },
      ]
    case 'spawnOneShot':
      return [{ kind: 'bus', id: action.busId }]
    case 'setRuleEnabled':
      return action.ruleIds.map((id) => ({ kind: 'rule' as const, id }))
    case 'setVariable':
    case 'showCard':
      return []
  }
}

/**
 * Where a rule must run. `auto` resolves from the triggers: anything depending
 * on the wall clock or an operator has to be decided by the server, and
 * everything else belongs on the listener's device, where it fires in
 * milliseconds and keeps working through a dropout.
 */
export function resolveRuleScope(rule: Rule): Scope {
  const forcesServer = rule.triggers.some((t) =>
    (SERVER_ONLY_TRIGGER_TYPES as readonly string[]).includes(t.type),
  )
  if (forcesServer) return 'session'
  return rule.scope === 'auto' ? 'listener' : rule.scope
}

/** True when the author asked for listener scope but the triggers cannot honour it. */
export function hasImpossibleScope(rule: Rule): boolean {
  return (
    rule.scope === 'listener' &&
    rule.triggers.some((t) => (SERVER_ONLY_TRIGGER_TYPES as readonly string[]).includes(t.type))
  )
}

function emitterRegionId(emitter: Emitter): string | undefined {
  return emitter.kind === 'zone' ? emitter.regionId : undefined
}

function duplicates(ids: readonly string[]): string[] {
  const seen = new Set<string>()
  const dupes = new Set<string>()
  for (const id of ids) {
    if (seen.has(id)) dupes.add(id)
    seen.add(id)
  }
  return [...dupes]
}

/**
 * Checks a world can actually run: every reference resolves, no id is claimed
 * twice, and nothing is authored at a scale the sensors cannot deliver.
 * Errors block publishing; warnings are advice worth reading.
 */
export function validateWorld(world: WorldDefinition): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const idLists: Record<RefKind, string[]> = {
    region: world.regions.map((r) => r.id),
    gate: world.gates.map((g) => g.id),
    course: world.courses.map((c) => c.id),
    emitter: world.emitters.map((e) => e.id),
    rule: world.rules.map((r) => r.id),
    cue: world.cues.map((c) => c.id),
    bus: world.buses.map((b) => b.id),
  }

  const known = Object.fromEntries(
    Object.entries(idLists).map(([kind, ids]) => [kind, new Set(ids)]),
  ) as Record<RefKind, Set<string>>

  for (const [kind, ids] of Object.entries(idLists) as [RefKind, string[]][]) {
    for (const id of duplicates(ids)) {
      issues.push({
        severity: 'error',
        code: 'duplicate-id',
        message: `More than one ${kind} uses the id "${id}".`,
        path: `${kind}s`,
      })
    }
  }

  const check = (ref: Ref, path: string): void => {
    if (!known[ref.kind].has(ref.id)) {
      issues.push({
        severity: 'error',
        code: 'unresolved-reference',
        message: `No ${ref.kind} with id "${ref.id}" exists in this world.`,
        path,
      })
    }
  }

  world.emitters.forEach((emitter, i) => {
    check({ kind: 'bus', id: emitter.busId }, `emitters[${i}].busId`)
    const regionId = emitterRegionId(emitter)
    if (regionId !== undefined) check({ kind: 'region', id: regionId }, `emitters[${i}].regionId`)

    if (emitter.kind === 'point' || emitter.kind === 'zone' || emitter.kind === 'path') {
      if (emitter.distance.maxDistanceM <= emitter.distance.refDistanceM) {
        issues.push({
          severity: 'error',
          code: 'invalid-distance-model',
          message: `"${emitter.name}" is inaudible: its cull radius (${emitter.distance.maxDistanceM} m) is not beyond its full-volume radius (${emitter.distance.refDistanceM} m).`,
          path: `emitters[${i}].distance`,
        })
      } else if (emitter.distance.maxDistanceM < MIN_RELIABLE_OUTDOOR_RADIUS_M) {
        issues.push({
          severity: 'warning',
          code: 'smaller-than-gps-error',
          message: `"${emitter.name}" is only audible within ${emitter.distance.maxDistanceM} m, which is inside typical GPS error. Outdoors, listeners may walk past without hearing it.`,
          path: `emitters[${i}].distance.maxDistanceM`,
        })
      }
    }
  })

  world.ducking.forEach((duck, i) => {
    check({ kind: 'bus', id: duck.sourceBusId }, `ducking[${i}].sourceBusId`)
    check({ kind: 'bus', id: duck.targetBusId }, `ducking[${i}].targetBusId`)
  })

  world.safetyZones.forEach((zone, i) => {
    check({ kind: 'region', id: zone.regionId }, `safetyZones[${i}].regionId`)
  })

  const declaredVariables = new Set(world.variables.map((v) => v.name))

  world.rules.forEach((rule, ruleIndex) => {
    rule.triggers.forEach((trigger, i) => {
      for (const ref of triggerRefs(trigger)) {
        check(ref, `rules[${ruleIndex}].triggers[${i}]`)
      }
      if (trigger.type === 'variable' && !declaredVariables.has(trigger.name)) {
        issues.push({
          severity: 'warning',
          code: 'undeclared-variable',
          message: `Rule "${rule.name}" reads the variable "${trigger.name}", which the world never declares. It will be treated as unset.`,
          path: `rules[${ruleIndex}].triggers[${i}].name`,
        })
      }
      if (trigger.type === 'proximity' && trigger.radiusM < MIN_RELIABLE_OUTDOOR_RADIUS_M) {
        issues.push({
          severity: 'warning',
          code: 'smaller-than-gps-error',
          message: `Rule "${rule.name}" waits for the listener to come within ${trigger.radiusM} m, which is inside typical GPS error. Outdoors this may fire late or not at all.`,
          path: `rules[${ruleIndex}].triggers[${i}].radiusM`,
        })
      }
    })

    rule.conditions.forEach((condition, i) => {
      if (condition.type === 'variable' && !declaredVariables.has(condition.name)) {
        issues.push({
          severity: 'warning',
          code: 'undeclared-variable',
          message: `Rule "${rule.name}" tests the variable "${condition.name}", which the world never declares.`,
          path: `rules[${ruleIndex}].conditions[${i}].name`,
        })
      }
    })

    rule.actions.forEach((action, i) => {
      for (const ref of actionRefs(action)) {
        check(ref, `rules[${ruleIndex}].actions[${i}]`)
      }
    })

    if (hasImpossibleScope(rule)) {
      issues.push({
        severity: 'error',
        code: 'impossible-scope',
        message: `Rule "${rule.name}" is set to run on the listener's device, but it waits on a trigger only the server can see. Set its scope to session or automatic.`,
        path: `rules[${ruleIndex}].scope`,
      })
    }
  })

  return issues
}

export function hasBlockingIssues(issues: readonly ValidationIssue[]): boolean {
  return issues.some((issue) => issue.severity === 'error')
}
